import { ArrowForwardIos } from '@mui/icons-material';
import { Button, type Theme, useTheme } from '@mui/material';
import { type SystemStyleObject } from '@mui/system';
import { useLayoutEffect, useRef, useState } from 'react';
import { useActiveOverviewDrawer } from '#hooks/useActiveOverviewDrawer.ts';
import { ContentWithLineBreaks } from '../ContentWithLineBreaks/index.tsx';
import { OverviewDrawer } from '../OverviewDrawer/index.tsx';
import { ButtonContainer, Container, ContentContainer } from './styled.ts';

export type OverviewProps = {
  content: string;
  contentType?: string;
  disableReadMore?: boolean;
  id: string | number;
  linesToClamp?: number;
  name: string;
  paragraphSx?: SystemStyleObject<Theme>;
  sx?: SystemStyleObject<Theme>;
};

export const Overview = ({
  content,
  contentType = 'overview',
  disableReadMore = false,
  id,
  linesToClamp = 5,
  name,
  paragraphSx,
  sx,
}: OverviewProps) => {
  const [activeOverviewDrawer, setActiveOverviewDrawer] = useActiveOverviewDrawer();
  const theme = useTheme();
  const [showReadMore, setShowReadMore] = useState<boolean>(false);
  const ref = useRef<HTMLParagraphElement>(null);

  useLayoutEffect(() => {
    if (!disableReadMore && ref.current) {
      const isTextClamped = ref.current.scrollHeight > ref.current.clientHeight;

      if (isTextClamped) {
        setShowReadMore(true);
      }
    }
  }, [disableReadMore, content]);

  return (
    <Container sx={sx}>
      {!disableReadMore && activeOverviewDrawer && activeOverviewDrawer.id === id ? (
        <OverviewDrawer
          content={content}
          name={name}
          setActiveOverviewDrawer={setActiveOverviewDrawer}
          titleSuffix={contentType}
        />
      ) : undefined}
      <ContentContainer
        ref={ref}
        sx={{
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: linesToClamp,
          display: '-webkit-box',
          marginBottom: theme.spacing(2),
          overflow: 'hidden',
          [theme.breakpoints.down('sm')]: {
            fontSize: '0.9rem',
          },
        }}
      >
        <ContentWithLineBreaks sx={{ ...paragraphSx, color: theme.mainTextPrimary }}>{content}</ContentWithLineBreaks>
      </ContentContainer>
      {!disableReadMore && showReadMore ? (
        <ButtonContainer>
          <Button
            onClick={() => {
              setActiveOverviewDrawer({ id });
            }}
          >
            Read more <ArrowForwardIos />
          </Button>
        </ButtonContainer>
      ) : undefined}
    </Container>
  );
};
